import { Case, Client, Role, User } from 'briefpoint-client';
import AddEditCaseModal from 'components/CaseManagement/AddEditCaseModal';
import AddEditClientModal from 'components/CaseManagement/AddEditClientModal';
import CaseTable from 'components/CaseManagement/CaseTable';
import Loading from 'components/Loading';
import { useCaseApi, useClientApi, useTagApi } from 'hooks/useApi';
import { useAuth } from 'hooks/useAuth';
import useFirm from 'hooks/useFirm';
import useJurisdictions from 'hooks/useJurisdictions';
import { useCallback, useEffect, useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import { Link, useLocation, useParams } from 'react-router-dom';
import './styles.scss';

type ClientParams = {
  id: string;
};

export default function ClientPage() {
  const { user, firm } = useAuth()!;
  const location = useLocation();
  const { id } = useParams<ClientParams>();
  const clientApi = useClientApi();
  const caseApi = useCaseApi();
  const tagApi = useTagApi();
  const [users, , , ,] = useFirm(firm?.id, false);
  const [showAddCase, setShowAddCase] = useState(false);
  const [showEditClient, setShowEditClient] = useState(false);
  const [cases, setCases] = useState<Case[] | undefined>();
  const [client, setClient] = useState<Client>((location.state as any)?.client);
  const [search, setSearch] = useState('');
  const [tags, setTags] = useState<Map<number, string> | undefined>();
  const jurisdictions = useJurisdictions();

  const loadClient = useCallback(async (user: User | undefined, client: Client | undefined) => {
    if (user && user.firmId && !client) {
      const client = await clientApi.clientGet({ firmId: user.firmId!, id });
      setClient(client);
    }
  }, [clientApi, id]);

  useEffect(() => {
    loadClient(user, client);
  }, [user, loadClient, client]);

  const loadCases = useCallback(async (user: User | undefined) => {
    if (user && user.firmId && id) {
      const cases = await caseApi.caseGetAll({ firmId: user.firmId!, clientId: id, usersCasesOnly: !user?.role?.includes(Role.FirmAdmin) });
      setCases(cases);
    }
  }, [caseApi, id]);

  useEffect(() => {
    loadCases(user);
  }, [user, loadCases]);

  useEffect(() => {
    async function loadTags() {
      const tagsList = new Map<number, string>();

      const loadedTags = await tagApi.tagFindTags({});

      loadedTags.forEach(t => tagsList.set(t.id, t.name));
      setTags(tagsList);
    };
    if (!tags) {
      loadTags();
    }
  }, [tagApi, tags]);

  async function AddCase(_case: Case) {
    const newCase = await caseApi.casePost({ firmId: user?.firmId!, clientId: id, _case });

    if (newCase) {
      if (cases) {
        setCases(c => [...c!, newCase]);
      } else {
        setCases([newCase]);
      }
    }

    closeAddCase();
  }

  function closeAddCase() {
    setShowAddCase(false);
  }

  async function saveClient(client: Client) {
    await clientApi.clientPut({ firmId: user?.firmId!, id: client.id!, client });

    setClient(client);
    setShowEditClient(false)
  }

  const filteredCases = cases?.filter(x => {
    if (!search) return true;

    const split = (x.title || '').concat(x.shortTitle || '').split(' ');

    for (let i = 0; i < split?.length; i++) {
      if (split[i].toLowerCase().startsWith(search.toLowerCase())) {
        return true;
      }
    }

    return false;
  });

  return (
    <>
      <Container className="page-container">
        <Loading isLoading={client === undefined && cases === undefined && jurisdictions === undefined && users === undefined}>
          <Row>
            <Col>
              <div className="mb-4 bread-crumbs"><Link to={'/clients'}>Clients</Link> {`> ${client?.name}`}</div></Col>
            <Col className='actions-cell'>
              <Button variant="primary" onClick={() => setShowAddCase(true)}>
                Add New Case
              </Button>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <div className='client-name-header'><p>{client?.name}</p></div>
              <Button variant="outline-secondary fs-6" onClick={() => setShowEditClient(true)}>
                Edit Client Details
              </Button>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col xs="4">
              <div className="input-group mb-2">
                <div className="input-group-prepend">
                  <div className="input-group-text">⌕</div>
                </div>
                <Form.Control
                  value={search}
                  className="form-control defense-search-input"
                  placeholder="Search cases"
                  onChange={(e) => setSearch(e.target.value.toLowerCase())}
                />
              </div>
            </Col>
          </Row>
          {((filteredCases?.length ?? 0) > 5) &&
            (<div><CaseTable cases={filteredCases?.slice().sort((a, b) => {
              if (a.lastActivity && b.lastActivity) {
                return a.lastActivity > b.lastActivity ? -1 : 1;
              }
              else if (a.lastActivity) {
                return -1;
              }
              else if (b.lastActivity) {
                return 1;
              }
              else {
                return 0;
              }
            }).slice(0, 5)} addCase={() => setShowAddCase(true)} client={client} users={users} jurisdictions={jurisdictions} showIfEmpty={false} />
              <span>&nbsp;&nbsp;</span>
              <h2>All Cases</h2>
            </div>)}
          <CaseTable cases={filteredCases?.slice().sort((a, b) => {
            if (a.shortTitle && b.shortTitle) {
              return a.shortTitle.localeCompare(b.shortTitle);
            }
            else if (a.shortTitle) {
              return -1;
            }
            else if (b.shortTitle) {
              return 1;
            }
            else {
              return 0;
            }
          })} addCase={() => setShowAddCase(true)} client={client} users={users} jurisdictions={jurisdictions} showIfEmpty={true} />
          {client &&
            <>
              <AddEditClientModal title='Edit Client' confirm='Save' isShow={showEditClient} onClose={() => setShowEditClient(false)} onConfirm={saveClient} client={client} users={users} />
              <AddEditCaseModal availableTags={tags} setAvailableTags={setTags} title='Add Case' confirm='Add' isShow={showAddCase} onClose={() => setShowAddCase(false)} onConfirm={AddCase} users={users} jurisdictions={jurisdictions!} client={client} />
            </>}
        </Loading>
      </Container>
    </>
  );
}
